import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';

import { useObservability } from '../../../helpers/observability';
import type { PropertySearchQuery } from '../helpers/useSearchQueries';
import { SAVED_SEARCHES_ENABLED } from './featureFlag';
import { formatSavedSearchName, hasValidLocation, toSavedSearchInput } from './mapping';
import { useSavedSearches } from './useSavedSearches';

export interface SaveSearchButtonProps {
  /**
   * The canonical filter shape for the search being saved. The parent must
   * merge `searchState.getCanonical()` with the active location (market,
   * zoneIds, or polygon vertices) before passing it in; `getCanonical()`
   * intentionally omits location.
   */
  query: PropertySearchQuery;
  variant?: 'primary' | 'secondary';
  size?: 'small' | 'medium';
  onSaved?: (savedSearchId: string) => void;
}

const SAVE_SEARCH_NAME_MAX_LENGTH = 80;
const NO_LOCATION_HINT = 'Pick a market, neighborhood, or area on the map first.';
const GENERIC_ERROR = 'Failed to save search. Please try again.';

// The marketplace doesn't have an in-page login modal, so unauthenticated
// users are bounced to `/login?redirectTo=...` matching `usePropertyReactions`.
const redirectToLogin = () => {
  const redirectTo = `${window.location.pathname}${window.location.search}`;
  window.location.assign(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
};

export const SaveSearchButton = ({
  query,
  variant = 'secondary',
  onSaved,
}: SaveSearchButtonProps) => {
  const { isLoggedIn, add } = useSavedSearches();
  const { trackEvent } = useObservability();

  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Used to drive focus into the name field once the Dialog portal mounts.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const nameInputId = useId();

  const validLocation = hasValidLocation(query);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Microtask defer so the Dialog content has time to mount before we
    // dispatch focus into it.
    queueMicrotask(() => {
      inputRef.current?.focus();
    });
  }, [isOpen]);

  const handleOpenDialog = useCallback(() => {
    if (!isLoggedIn) {
      redirectToLogin();
      return;
    }
    setName(formatSavedSearchName(query));
    setErrorMessage(null);
    setIsOpen(true);
  }, [isLoggedIn, query]);

  const handleClose = useCallback(() => {
    if (submitting) {
      return;
    }
    setIsOpen(false);
  }, [submitting]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) {
      return;
    }
    setSubmitting(true);
    setErrorMessage(null);
    const result = await add(toSavedSearchInput(query, trimmedName));
    setSubmitting(false);
    if (result.success) {
      trackEvent('cta-click', 'save-search-created', {
        savedSearchId: result.savedSearch?.id,
        market: query.market,
        zoneIdsCount: query.zoneIds?.length ?? 0,
        verticesCount: query.vertices?.length ?? 0,
      });
      setIsOpen(false);
      onSaved?.(result.savedSearch?.id ?? '');
      return;
    }
    setErrorMessage(result.errorMessage ?? GENERIC_ERROR);
  }, [add, name, onSaved, query, submitting, trackEvent]);

  const trimmedName = name.trim();
  const canSubmit = !submitting && trimmedName.length > 0;
  const triggerHint = validLocation ? undefined : NO_LOCATION_HINT;

  let submitLabel = 'Save';
  if (submitting) {
    submitLabel = 'Saving…';
  }

  if (!SAVED_SEARCHES_ENABLED) {
    return null;
  }

  return (
    <>
      <Button
        analyticsName="save-search-button"
        onClick={handleOpenDialog}
        size="lg"
        variant={variant}
        disabled={!validLocation}
        aria-label={validLocation ? 'Save search' : `Save search. ${triggerHint}`}
        className="max-md:min-w-0 max-md:flex-1"
      >
        Save search
      </Button>
      <Dialog.Root
        role="dialog"
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
        disablePointerDismissal={submitting}
      >
        <Dialog title="Name your saved search">
          <Dialog.Description className="text-body-md text-od-content-secondary">
            Give your search a name so you can find it later.
          </Dialog.Description>
          <Col className="pt-4 pb-2">
            <Col className="gap-1.5">
              <Text
                as="label"
                htmlFor={nameInputId}
                className="text-label-sm text-od-content-primary"
              >
                Search name
              </Text>
              <Input
                ref={inputRef}
                id={nameInputId}
                size="base"
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
                analyticsName="save-search-name-input"
                placeholder="My saved search"
              />
            </Col>
            {errorMessage && (
              <Text as="p" role="alert" className="pt-2 text-body-sm text-od-content-error">
                {errorMessage}
              </Text>
            )}
          </Col>
          <Row className="justify-end gap-2 pt-6">
            <Button
              variant="secondary"
              size="md"
              aria-label="Cancel"
              analyticsName="save-search-cancel"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="md"
              aria-label={submitLabel}
              analyticsName="save-search-submit"
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              {submitLabel}
            </Button>
          </Row>
        </Dialog>
      </Dialog.Root>
    </>
  );
};
